/////////////////////////////////////////////////////////////////////
// Copyright (c) Autodesk, Inc. All rights reserved
// Written by Autodesk Inventor Automation team
//
// Permission to use, copy, modify, and distribute this software in
// object code form for any purpose and without fee is hereby granted,
// provided that the above copyright notice appears in all copies and
// that both that copyright notice and the limited warranty and
// restricted rights notice below appear in all supporting
// documentation.
//
// AUTODESK PROVIDES THIS PROGRAM "AS IS" AND WITH ALL FAULTS.
// AUTODESK SPECIFICALLY DISCLAIMS ANY IMPLIED WARRANTY OF
// MERCHANTABILITY OR FITNESS FOR A PARTICULAR USE.  AUTODESK, INC.
// DOES NOT WARRANT THAT THE OPERATION OF THE PROGRAM WILL BE
// UNINTERRUPTED OR ERROR FREE.
/////////////////////////////////////////////////////////////////////
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import Surface from '@hig/surface';
import './app.css';
import Toolbar from './components/toolbar';
import TabsContainer from './components/tabsContainer';
import ProjectSwitcher from './components/projectSwitcher';
import { showAdoptWithParamsFailed, fetchShowParametersChanged } from './actions/uiFlagsActions';
import { detectCode } from './actions/profileActions';
import ModalProgress from './components/modalProgress';
import { adoptWithParamsFailed, embeddedModeEnabled, embeddedModeUrl, adoptWithParamsProgressShowing, errorData } from './reducers/mainReducer';
import { adoptProjectWithParameters } from './actions/adoptWithParamsActions';
import Login from './components/Login';
import CompanyRegistration from './components/CompanyRegistration/CompanyRegistration';
import ResetPassword from './components/ResetPassword/ResetPassword';
import MaintenancePage from './components/MaintenancePage/MaintenancePage';
import authService from './services/authService';
import settingsService from './services/settingsService';
import QuoteCart from './components/QuoteCart/QuoteCart';
import quoteCartService from './services/quoteCartService';

export class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isAuthenticated: authService.isAuthenticated(),
      user: authService.getCurrentUser(),
      cartCount: 0,
      showQuoteCart: false,
      anonymousAccess: false,
      rfqMode: true,
      settingsLoaded: false,
      maintenanceMode: false,
      maintenanceMessage: '',
      showAdminLogin: false,
      loginError: ''
    };
    props.detectCode();
    this.loadCartCount = this.loadCartCount.bind(this);
    this.openQuoteCart = this.openQuoteCart.bind(this);
    this.closeQuoteCart = this.closeQuoteCart.bind(this);
  }

  async componentDidMount() {
    // Fetch public settings (no auth required)
    try {
      const publicSettings = await settingsService.getPublicSettings();
      // Set browser tab title from subdomain or fallback to ConfigSelect
      const subdomain = settingsService.getSubdomain();
      if (subdomain) {
        document.title = `${subdomain}.configselect.com`;
      }

      this.setState({
        anonymousAccess: publicSettings.anonymous_access === 'true',
        rfqMode: publicSettings.rfq_mode !== 'false',
        maintenanceMode: publicSettings.maintenance_mode === 'true',
        maintenanceMessage: publicSettings.maintenance_message || '',
        settingsLoaded: true
      });
    } catch {
      this.setState({ settingsLoaded: true });
    }

    // Apply saved color settings + check maintenance for already-authenticated users
    if (this.state.isAuthenticated) {
      try {
        const allSettings = await settingsService.getAll();
        App.applyColorSettings(allSettings);

        // If user's company is in maintenance and they're not an admin, force logout
        if (allSettings.maintenance_mode === 'true' && !authService.isAdmin()) {
          authService.logout();
          this.setState({
            isAuthenticated: false,
            user: null,
            maintenanceMode: true,
            maintenanceMessage: allSettings.maintenance_message || '',
            loginError: 'This site is currently under maintenance. Please try again later.',
            showAdminLogin: true
          });
          return;
        }
      } catch {
        // Use defaults
      }
    }

    if (!this.props.embeddedModeEnabled)
      this.props.fetchShowParametersChanged();
    if (this.props.embeddedModeUrl != null)
      this.props.adoptProjectWithParameters(this.props.embeddedModeUrl);
    if (this.state.isAuthenticated) {
      this.loadCartCount();
    }
  }

  async loadCartCount() {
    // Only load if user is authenticated and has a token
    const token = localStorage.getItem('token');
    if (!token) {
      this.setState({ cartCount: 0 });
      return;
    }

    try {
      const result = await quoteCartService.getCartCount();
      this.setState({ cartCount: result.count || 0 });
    } catch (err) {
      console.error('Failed to load cart count:', err);
      this.setState({ cartCount: 0 });
    }
  }

  openQuoteCart() {
    this.setState({ showQuoteCart: true });
  }

  closeQuoteCart() {
    this.setState({ showQuoteCart: false });
  }

  handleLoginSuccess = (user) => {
    // If maintenance mode is on and user is not an admin, deny access
    const isAdmin = user.role === 'Administrator' || user.role === 'System Administrator' || user.role === 'Company Administrator';
    if (this.state.maintenanceMode && !isAdmin) {
      authService.logout();
      this.setState({
        loginError: 'This site is currently under maintenance. Please try again later.',
        showAdminLogin: true
      });
      return;
    }

    this.setState({
      isAuthenticated: true,
      user: user,
      loginError: '',
      showAdminLogin: false
    }, () => {
      this.loadCartCount();
    });
  }

  static applyColorSettings(settings) {
    const root = document.documentElement;
    if (settings.primary_color_1) root.style.setProperty('--primary-color-1', settings.primary_color_1);
    if (settings.primary_color_2) root.style.setProperty('--primary-color-2', settings.primary_color_2);
    if (settings.secondary_color_1) root.style.setProperty('--secondary-color-1', settings.secondary_color_1);
    if (settings.secondary_color_2) root.style.setProperty('--secondary-color-2', settings.secondary_color_2);
  }

  render () {
    const showToolbar = !this.props.embeddedModeEnabled;
    const isAnonymous = !this.state.isAuthenticated && this.state.anonymousAccess;
    const rfqMode = this.state.rfqMode;
    const hideQuoteButtons = isAnonymous && !rfqMode;

    const MainApp = () => (
      <Surface className="fullheight" id="main" level={200}>
        { showToolbar &&
          <Toolbar
            cartCount={this.state.cartCount}
            onCartClick={hideQuoteButtons ? null : this.openQuoteCart}
            isAnonymous={isAnonymous}
          >
            {authService.isAdmin() && <ProjectSwitcher />}
          </Toolbar>
        }
        <TabsContainer
          onCartUpdated={this.loadCartCount}
          isAnonymous={isAnonymous}
          hideQuoteButtons={hideQuoteButtons}
        />
        {this.props.adoptWithParamsProgressShowing &&
          <ModalProgress
              open={true}
              title="Loading Content"
              label=" "/>
        }
        {!hideQuoteButtons &&
          <QuoteCart
            isOpen={this.state.showQuoteCart}
            onClose={this.closeQuoteCart}
            onCartUpdated={this.loadCartCount}
          />
        }
      </Surface>
    );

    return (
      <Router>
        <Switch>
          <Route path="/login" render={() => {
            if (this.state.isAuthenticated) {
              return <Redirect to="/" />;
            }
            if (this.state.maintenanceMode && !this.state.showAdminLogin) {
              return <MaintenancePage
                message={this.state.maintenanceMessage}
                onAdminLogin={() => this.setState({ showAdminLogin: true, loginError: '' })}
              />;
            }
            return <Login onLoginSuccess={this.handleLoginSuccess} loginError={this.state.loginError} />;
          }} />
          <Route path="/register-company" component={CompanyRegistration} />
          <Route path="/reset-password" component={ResetPassword} />
          <Route path="/" render={() => {
            if (this.state.maintenanceMode && !this.state.isAuthenticated) {
              return <Redirect to="/login" />;
            }
            if (this.state.isAuthenticated || this.state.anonymousAccess) {
              return <MainApp />;
            }
            if (!this.state.settingsLoaded) {
              // Show nothing briefly while settings load, then redirect
              return <div style={{ background: '#1a1a2e', minHeight: '100vh' }} />;
            }
            return <Redirect to="/login" />;
          }} />
        </Switch>
      </Router>
    );
  }
}

/* istanbul ignore next */
export default connect(function (store) {
  return {
    adoptWithParamsProgressShowing: adoptWithParamsProgressShowing(store),
    adoptWithParamsFailed: adoptWithParamsFailed(store),
    embeddedModeEnabled: embeddedModeEnabled(store),
    embeddedModeUrl: embeddedModeUrl(store),
    errorData: errorData(store)
  };}, {
    showAdoptWithParamsFailed, adoptProjectWithParameters, fetchShowParametersChanged, detectCode
})(App);
